// ─── Quarterly Summary Modal ───────────────────────────────────────────────
// Partner-facing pop-up triggered by the "Summarize Quarter" button on the
// Home dashboard's Active Pipeline section. Shows Q1/Q2/Q3/Q4 tabs across
// the top; each quarter's tab shows:
//   - Big number tiles: # active deals, total SF, total ask $, advanced count
//   - Off-market vs on-market split with SF + $ subtotals
//   - "Missing asking price" list — deals to fill in manually
//   - Full deal table (scrollable)
//   - Export → 2-sheet branded XLSX (summary + deal list)
//
// "Active in the quarter" = the deal was in one of the active stages
// (Screening / Outreach / Underwriting / LOI Submitted / In Diligence) at
// ANY point during the quarter window — reconstructed from activity_log.

import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx-js-style";
import { supabase } from "../../supabase";

const ACTIVE_STAGES   = new Set(["Screening", "Sourcing", "Outreach", "Underwriting", "LOI Submitted", "In Diligence"]);
const ADVANCED_STAGES = new Set(["Underwriting", "LOI Submitted", "In Diligence"]);
// Terminal-stage set — deals in these stages during the quarter get their own
// section AND get expandable kill-detail rows on the deal list.
const KILLED_STAGES   = new Set(["Passed", "Lost", "Dead"]);

// Match Comp Tables branding (RGB hex — xlsx-js-style expects no alpha)
const PP = {
  red: "E93529", redDark: "C42D22", dark: "333333", light: "F4F4F4",
  gray: "D8D8D8", white: "FFFFFF", whiteRGB: "FFFFFF", mutedText: "6B7280",
};

function fmtMoney(n) {
  if (!n || !Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtSF(n) { return n ? `${Number(n).toLocaleString()} SF` : "—"; }
function parseDealName(notes) { return notes?.match(/^Name:\s*(.+)$/m)?.[1] || null; }

// Compact key/value row used inside the killed-deal expanded panel.
function KV({ k, v, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
      <span style={{ color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{k}</span>
      <span style={{ color: accent ? "#c42d22" : "#333", fontWeight: accent ? 700 : 500 }}>{v}</span>
    </div>
  );
}

// Quarter boundaries in the year (UTC ms).
function quarterBounds(year, q) {
  const startMonth = (q - 1) * 3;
  const startMs = Date.UTC(year, startMonth, 1, 0, 0, 0);
  const endMs = Date.UTC(year, startMonth + 3, 1, 0, 0, 0) - 1;   // last ms of quarter
  return { startMs, endMs };
}

export default function QuarterlySummaryModal({ open, onClose, deals, stageLog, defaultQuarter }) {
  const now = new Date();
  const year = now.getUTCFullYear();
  const currentQuarter = Math.floor(now.getUTCMonth() / 3) + 1;

  const [selectedQ, setSelectedQ] = useState(defaultQuarter || currentQuarter);

  // Competition rows keyed by deal_id — loaded lazily whenever the visible
  // quarter's set of killed deals changes. One query per quarter switch,
  // never per row, so the expandable view is always instant.
  const [competitionByDeal, setCompetitionByDeal] = useState({});
  // Which killed deal is currently expanded on the deal list. Null when
  // none is open. Only one at a time to keep the modal navigable.
  const [expandedDealId, setExpandedDealId] = useState(null);

  // ESC + backdrop close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Build stage timeline per deal — same reconstruction as HomeDashboard
  const timeline = useMemo(() => {
    const tl = new Map();
    for (const e of stageLog || []) {
      const id = e.entity_id;
      const stage = e.action === "deal_created" ? e.metadata?.stage : e.metadata?.new_stage;
      if (!id || !stage) continue;
      if (!tl.has(id)) tl.set(id, []);
      tl.get(id).push({ at: new Date(e.created_at).getTime(), stage });
    }
    return tl;
  }, [stageLog]);

  const stageAt = (deal, atMs) => {
    if (!deal.createdAt || new Date(deal.createdAt).getTime() > atMs) return null;
    const tl = timeline.get(deal.id) || [];
    let latest = null;
    for (const ev of tl) { if (ev.at <= atMs) latest = ev.stage; else break; }
    return latest ?? deal.stage;
  };

  // "Was the deal in-scope for this quarter?" — TRUE if either:
  //   (a) the deal was in an active stage at any point in [qStart, qEnd], OR
  //   (b) the deal was killed (Passed / Lost / Dead) inside the quarter.
  // Case (b) surfaces post-mortem review — kills that happened this quarter
  // belong in this quarter's summary even if the active period was earlier.
  const wasActiveInQuarter = (deal, qStartMs, qEndMs) => {
    if (!deal.createdAt) return false;
    const createdMs = new Date(deal.createdAt).getTime();
    if (createdMs > qEndMs) return false;
    const startCheck = Math.max(qStartMs, createdMs);
    const samples = [startCheck, qEndMs];
    const tl = timeline.get(deal.id) || [];
    for (const ev of tl) {
      if (ev.at >= startCheck && ev.at <= qEndMs) samples.push(ev.at);
    }
    const anyActive = samples.some(t => {
      const s = stageAt(deal, t);
      return s && ACTIVE_STAGES.has(s);
    });
    if (anyActive) return true;
    // Case (b): killed_at falls inside the quarter, OR any transition into a
    // killed stage happened inside the quarter.
    const killedAtMs = deal.killedAt ? new Date(deal.killedAt).getTime() : null;
    if (killedAtMs && killedAtMs >= qStartMs && killedAtMs <= qEndMs) return true;
    return tl.some(ev => ev.at >= qStartMs && ev.at <= qEndMs && KILLED_STAGES.has(ev.stage));
  };

  // Compute the per-quarter deal set (memo keyed on the selected quarter)
  const q = useMemo(() => {
    const { startMs, endMs } = quarterBounds(year, selectedQ);
    const dealsInQ = (deals || [])
      .filter(d => wasActiveInQuarter(d, startMs, endMs))
      .map(d => ({
        ...d,
        _endStage: stageAt(d, Math.min(endMs, now.getTime())),   // stage at end of quarter (or now if q ongoing)
        _isAdvanced: (() => {
          const s = stageAt(d, Math.min(endMs, now.getTime()));
          return s && ADVANCED_STAGES.has(s);
        })(),
      }));

    const marketed = dealsInQ.filter(d => d.marketed);
    const offMarket = dealsInQ.filter(d => !d.marketed);
    const missingPrice = dealsInQ.filter(d => !d.askingPrice || d.askingPrice <= 0);

    const totalSF = dealsInQ.reduce((s, d) => s + (Number(d.squareFeet) || 0), 0);
    const totalAsk = dealsInQ.reduce((s, d) => s + (Number(d.askingPrice) || 0), 0);
    const mktSF = marketed.reduce((s, d) => s + (Number(d.squareFeet) || 0), 0);
    const mktAsk = marketed.reduce((s, d) => s + (Number(d.askingPrice) || 0), 0);
    const offSF = offMarket.reduce((s, d) => s + (Number(d.squareFeet) || 0), 0);
    const offAsk = offMarket.reduce((s, d) => s + (Number(d.askingPrice) || 0), 0);

    // Killed deals in-scope for this quarter — used by the expandable Kill
    // Detail view + the export's Kill Detail sheet.
    const killed = dealsInQ.filter(d => KILLED_STAGES.has(d._endStage || d.stage));

    return {
      startMs, endMs,
      startLabel: new Date(startMs).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }),
      endLabel: new Date(endMs).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }),
      deals: dealsInQ,
      marketed, offMarket, missingPrice,
      totalSF, totalAsk, mktSF, mktAsk, offSF, offAsk,
      advanced: dealsInQ.filter(d => d._isAdvanced),
      killed,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQ, deals, timeline]);

  // Load deal_competition rows for every killed deal in the current quarter.
  // One .in() query per quarter switch. Keeps a stable object keyed by
  // deal_id so the expand-on-click view + the export both read from the
  // same source of truth.
  useEffect(() => {
    if (!open) return;
    const ids = q.killed.map(d => d.id);
    if (ids.length === 0) { setCompetitionByDeal({}); return; }
    let cancelled = false;
    supabase.from("deal_competition")
      .select("*")
      .in("deal_id", ids)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.warn("competition load failed:", error.message); return; }
        const map = {};
        (data || []).forEach(row => { map[row.deal_id] = row; });
        setCompetitionByDeal(map);
      });
    return () => { cancelled = true; };
  }, [open, q.killed]);

  // Close any open expansion when the user switches quarters — otherwise a
  // stale deal id can be visually "open" on a quarter that doesn't contain it.
  useEffect(() => { setExpandedDealId(null); }, [selectedQ]);

  // ── Excel export ────────────────────────────────────────────────
  function exportXLSX() {
    const wb = XLSX.utils.book_new();
    wb.Props = { Title: `Q${selectedQ} ${year} Pipeline Summary`, Author: "ProximityParks", Company: "ProximityParks", CreatedDate: new Date() };

    // ── Sheet 1: Summary ──────────────────────────────────────
    const summaryRows = [
      ["ProximityParks  ·  Quarterly Pipeline Summary"],
      [`Q${selectedQ} ${year}  ·  ${q.startLabel} — ${q.endLabel}  ·  Exported ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`],
      [],
      ["METRIC", "TOTAL", "MARKETED", "OFF-MARKET"],
      ["Deals Active in Quarter", q.deals.length, q.marketed.length, q.offMarket.length],
      ["Total Square Footage", q.totalSF, q.mktSF, q.offSF],
      ["Total Asking Price ($)", q.totalAsk, q.mktAsk, q.offAsk],
      ["Advanced (UW/LOI/DD)", q.advanced.length, q.advanced.filter(d => d.marketed).length, q.advanced.filter(d => !d.marketed).length],
      ["Missing Asking Price", q.missingPrice.length, "", ""],
    ];
    const sh1 = XLSX.utils.aoa_to_sheet(summaryRows);
    // Style row 1 — brand banner
    sh1["A1"].s = { font: { name: "Montserrat", bold: true, sz: 16, color: { rgb: PP.whiteRGB } }, fill: { patternType: "solid", fgColor: { rgb: PP.red } }, alignment: { vertical: "center", horizontal: "left", indent: 1 } };
    sh1["A2"].s = { font: { name: "Montserrat", sz: 10, color: { rgb: PP.whiteRGB } }, fill: { patternType: "solid", fgColor: { rgb: PP.dark } }, alignment: { vertical: "center", horizontal: "left", indent: 1 } };
    // Header row (row 4)
    for (const c of ["A4", "B4", "C4", "D4"]) {
      if (!sh1[c]) continue;
      sh1[c].s = {
        font: { name: "Montserrat", bold: true, sz: 10, color: { rgb: PP.whiteRGB } },
        fill: { patternType: "solid", fgColor: { rgb: PP.red } },
        alignment: { vertical: "center", horizontal: "left" },
        border: { top: { style: "thin", color: { rgb: PP.redDark } }, bottom: { style: "thin", color: { rgb: PP.redDark } }, left: { style: "thin", color: { rgb: PP.redDark } }, right: { style: "thin", color: { rgb: PP.redDark } } },
      };
    }
    // Body rows
    for (let r = 5; r <= 9; r++) {
      for (const col of ["A", "B", "C", "D"]) {
        const addr = `${col}${r}`;
        if (!sh1[addr]) continue;
        sh1[addr].s = {
          font: { name: "Montserrat", sz: 10, color: { rgb: PP.dark }, bold: col === "A" },
          fill: (r % 2 === 0) ? { patternType: "solid", fgColor: { rgb: PP.light } } : undefined,
          alignment: { vertical: "center", horizontal: col === "A" ? "left" : "right" },
          border: { bottom: { style: "thin", color: { rgb: PP.gray } } },
        };
        if (r === 6 && col !== "A") sh1[addr].s.numFmt = '#,##0';
        if (r === 7 && col !== "A") sh1[addr].s.numFmt = '"$"#,##0';
      }
    }
    sh1["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
    ];
    sh1["!rows"] = [{ hpt: 32 }, { hpt: 18 }, { hpt: 8 }, { hpt: 22 }];
    sh1["!cols"] = [{ wch: 32 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
    sh1["!sheetViews"] = [{ showGridLines: false }];
    sh1["!ref"] = "A1:D9";
    XLSX.utils.book_append_sheet(wb, sh1, `Q${selectedQ} Summary`);

    // ── Sheet 2: Deal list ─────────────────────────────────────
    const dealHeader = ["Deal Name", "Address", "Market", "Stage @ Q-end", "SF", "Asking Price ($)", "Sourcing", "Created", "Deal URL"];
    const dealRows = q.deals
      .slice()
      .sort((a, b) => (b.askingPrice || 0) - (a.askingPrice || 0))
      .map(d => {
        const name = parseDealName(d.sourceNotes) || d.addresses?.[0]?.split(",")[0] || "(unnamed)";
        const addr = d.addresses?.[0] || "";
        return [
          name,
          addr,
          d.market || "",
          d._endStage || d.stage || "",
          Number(d.squareFeet) || 0,
          Number(d.askingPrice) || 0,
          d.marketed ? "On-Market" : "Off-Market",
          d.createdAt ? d.createdAt.slice(0, 10) : "",
          `https://proxy.proximityparks.com/deal/${d.id}`,
        ];
      });
    const sh2Aoa = [
      ["ProximityParks  ·  Q" + selectedQ + " " + year + " Deal List"],
      [`${q.deals.length} deals active in this quarter  ·  Exported ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`],
      [],
      dealHeader,
      ...dealRows,
    ];
    const sh2 = XLSX.utils.aoa_to_sheet(sh2Aoa);
    sh2["A1"].s = { font: { name: "Montserrat", bold: true, sz: 16, color: { rgb: PP.whiteRGB } }, fill: { patternType: "solid", fgColor: { rgb: PP.red } }, alignment: { vertical: "center", horizontal: "left", indent: 1 } };
    sh2["A2"].s = { font: { name: "Montserrat", sz: 10, color: { rgb: PP.whiteRGB } }, fill: { patternType: "solid", fgColor: { rgb: PP.dark } }, alignment: { vertical: "center", horizontal: "left", indent: 1 } };
    // Header
    dealHeader.forEach((_, i) => {
      const addr = XLSX.utils.encode_cell({ r: 3, c: i });
      if (!sh2[addr]) return;
      sh2[addr].s = {
        font: { name: "Montserrat", bold: true, sz: 9, color: { rgb: PP.whiteRGB } },
        fill: { patternType: "solid", fgColor: { rgb: PP.red } },
        alignment: { vertical: "center", horizontal: "left" },
        border: { top: { style: "thin", color: { rgb: PP.redDark } }, bottom: { style: "thin", color: { rgb: PP.redDark } }, left: { style: "thin", color: { rgb: PP.redDark } }, right: { style: "thin", color: { rgb: PP.redDark } } },
      };
    });
    // Body
    dealRows.forEach((row, ri) => {
      const stripe = ri % 2 === 1;
      row.forEach((_, ci) => {
        const addr = XLSX.utils.encode_cell({ r: 4 + ri, c: ci });
        if (!sh2[addr]) return;
        const isNumeric = ci === 4 || ci === 5;
        sh2[addr].s = {
          font: { name: "Montserrat", sz: 10, color: { rgb: PP.dark }, bold: ci === 0 },
          alignment: { vertical: "center", horizontal: isNumeric ? "right" : "left", wrapText: false },
          border: { bottom: { style: "thin", color: { rgb: PP.gray } } },
          ...(stripe ? { fill: { patternType: "solid", fgColor: { rgb: PP.light } } } : {}),
          ...(ci === 4 ? { numFmt: '#,##0' } : {}),
          ...(ci === 5 ? { numFmt: '"$"#,##0' } : {}),
        };
      });
    });
    sh2["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: dealHeader.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: dealHeader.length - 1 } },
    ];
    sh2["!rows"] = [{ hpt: 32 }, { hpt: 18 }, { hpt: 8 }, { hpt: 22 }];
    sh2["!cols"] = [{ wch: 28 }, { wch: 40 }, { wch: 22 }, { wch: 16 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 48 }];
    sh2["!sheetViews"] = [{ showGridLines: false, state: "frozen", ySplit: 4, topLeftCell: "A5" }];
    sh2["!ref"] = `A1:${XLSX.utils.encode_cell({ r: 3 + dealRows.length, c: dealHeader.length - 1 })}`;
    XLSX.utils.book_append_sheet(wb, sh2, "Deal List");

    // ── Sheet 3: Kill Detail (Passed + Lost + Dead) ─────────────
    // One expanded row per killed deal — reason, date, narrative, and (when
    // present) the full bid stack from deal_competition. Rendered even when
    // there's nothing to report, so the sheet is a reliable place to look.
    if (q.killed && q.killed.length > 0) {
      const killedHeader = [
        "Deal Name", "Address", "Stage", "Reason", "Killed Date",
        "Est. Bidders", "Best & Final",
        "Our Bid $/SF", "Our Bid Total",
        "Winning $/SF", "Winning Total",
        "Δ $/SF", "Δ %",
        "Winner", "Narrative", "Broker Debrief",
      ];
      // Sort: Lost first (most useful for review), then Passed, then Dead.
      // Within each stage, most-recent kill first.
      const stageWeight = { Lost: 0, Passed: 1, Dead: 2 };
      const killedSorted = q.killed.slice().sort((a, b) => {
        const stageDiff = (stageWeight[a._endStage || a.stage] ?? 3) - (stageWeight[b._endStage || b.stage] ?? 3);
        if (stageDiff !== 0) return stageDiff;
        const ka = a.killedAt ? new Date(a.killedAt).getTime() : 0;
        const kb = b.killedAt ? new Date(b.killedAt).getTime() : 0;
        return kb - ka;
      });
      const killedRows = killedSorted.map(d => {
        const name = parseDealName(d.sourceNotes) || d.addresses?.[0]?.split(",")[0] || "(unnamed)";
        const comp = competitionByDeal[d.id] || {};
        const killedDate = d.killedAt ? new Date(d.killedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "";
        return [
          name,
          d.addresses?.[0] || "",
          d._endStage || d.stage || "",
          d.killedReason || "",
          killedDate,
          comp.num_bidders_estimated ?? "",
          comp.made_best_and_final === true ? "Yes" : comp.made_best_and_final === false ? "No" : "",
          comp.our_final_bid_psf ?? "",
          comp.our_final_bid_total ?? "",
          comp.winning_bid_psf ?? "",
          comp.winning_bid_total ?? "",
          comp.delta_to_winner_psf ?? "",
          comp.delta_pct ?? "",
          comp.winner_name || "",
          d.killedNarrative || "",
          comp.broker_debrief_notes || "",
        ];
      });
      const sh3Aoa = [
        [`ProximityParks  ·  Q${selectedQ} ${year} Kill Detail`],
        [`${killedSorted.length} deals killed in this quarter  ·  Passed / Lost / Dead  ·  Exported ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`],
        [],
        killedHeader,
        ...killedRows,
      ];
      const sh3 = XLSX.utils.aoa_to_sheet(sh3Aoa);
      sh3["A1"].s = { font: { name: "Montserrat", bold: true, sz: 16, color: { rgb: PP.whiteRGB } }, fill: { patternType: "solid", fgColor: { rgb: PP.red } }, alignment: { vertical: "center", horizontal: "left", indent: 1 } };
      sh3["A2"].s = { font: { name: "Montserrat", sz: 10, color: { rgb: PP.whiteRGB } }, fill: { patternType: "solid", fgColor: { rgb: PP.dark } }, alignment: { vertical: "center", horizontal: "left", indent: 1 } };
      killedHeader.forEach((_, i) => {
        const addr = XLSX.utils.encode_cell({ r: 3, c: i });
        if (!sh3[addr]) return;
        sh3[addr].s = {
          font: { name: "Montserrat", bold: true, sz: 9, color: { rgb: PP.whiteRGB } },
          fill: { patternType: "solid", fgColor: { rgb: PP.red } },
          alignment: { vertical: "center", horizontal: "left" },
          border: { top: { style: "thin", color: { rgb: PP.redDark } }, bottom: { style: "thin", color: { rgb: PP.redDark } }, left: { style: "thin", color: { rgb: PP.redDark } }, right: { style: "thin", color: { rgb: PP.redDark } } },
        };
      });
      // Column format hints: numerics for bid columns, currency for totals, percent for Δ%
      const currencyCols = new Set([8, 10]);   // Our Bid Total, Winning Total
      const psfCols      = new Set([7, 9, 11]); // $/SF cells + delta $/SF
      const pctCols      = new Set([12]);       // Δ %
      const numericCols  = new Set([5, ...currencyCols, ...psfCols, ...pctCols]);
      killedRows.forEach((row, ri) => {
        const stripe = ri % 2 === 1;
        row.forEach((_, ci) => {
          const addr = XLSX.utils.encode_cell({ r: 4 + ri, c: ci });
          if (!sh3[addr]) return;
          const isNarrative = ci === 14 || ci === 15;
          sh3[addr].s = {
            font: { name: "Montserrat", sz: 10, color: { rgb: PP.dark }, bold: ci === 0 },
            alignment: { vertical: "top", horizontal: numericCols.has(ci) ? "right" : "left", wrapText: isNarrative },
            border: { bottom: { style: "thin", color: { rgb: PP.gray } } },
            ...(stripe ? { fill: { patternType: "solid", fgColor: { rgb: PP.light } } } : {}),
            ...(currencyCols.has(ci) ? { numFmt: '"$"#,##0' } : {}),
            ...(psfCols.has(ci) ? { numFmt: '"$"#,##0.00' } : {}),
            ...(pctCols.has(ci) ? { numFmt: '0.0"%"' } : {}),
          };
        });
      });
      sh3["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: killedHeader.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: killedHeader.length - 1 } },
      ];
      sh3["!rows"] = [{ hpt: 32 }, { hpt: 18 }, { hpt: 8 }, { hpt: 22 }];
      sh3["!cols"] = [
        { wch: 28 }, { wch: 40 }, { wch: 10 }, { wch: 26 }, { wch: 14 },
        { wch: 10 }, { wch: 12 },
        { wch: 12 }, { wch: 14 },
        { wch: 12 }, { wch: 14 },
        { wch: 10 }, { wch: 8 },
        { wch: 22 }, { wch: 50 }, { wch: 40 },
      ];
      sh3["!sheetViews"] = [{ showGridLines: false, state: "frozen", ySplit: 4, topLeftCell: "A5" }];
      sh3["!ref"] = `A1:${XLSX.utils.encode_cell({ r: 3 + killedRows.length, c: killedHeader.length - 1 })}`;
      XLSX.utils.book_append_sheet(wb, sh3, "Kill Detail");
    }

    XLSX.writeFile(wb, `PPUSA_Q${selectedQ}_${year}_pipeline.xlsx`);
  }

  if (!open) return null;

  const Tile = ({ label, value, sub, accent }) => (
    <div className="rounded-lg p-3" style={{ background: "#ffffff", border: "1px solid #e4e4e4" }}>
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "#888" }}>{label}</p>
      <p className="text-2xl font-bold leading-none" style={{ color: accent ? "#e93529" : "#333" }}>{value}</p>
      {sub && <p className="text-[11px] mt-1" style={{ color: "#888" }}>{sub}</p>}
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "stretch", justifyContent: "center", padding: 24, fontFamily: "'Montserrat', sans-serif" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ flex: 1, maxWidth: 1400, background: "#f4f4f4", color: "#333", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", overflow: "hidden", border: "1px solid #d8d8d8" }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #e4e4e4", background: "#fff" }}>
          <div className="flex items-center gap-3">
            <div style={{ width: 6, height: 32, borderRadius: 3, background: "#e93529" }} />
            <div>
              <h2 className="text-base font-bold" style={{ color: "#333" }}>Quarterly Pipeline Summary — {year}</h2>
              <p className="text-xs mt-0.5" style={{ color: "#888" }}>Active at any point during the quarter · Total SF + Asking Price basis</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportXLSX}
              disabled={q.deals.length === 0}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: "#fff", background: "#e93529", opacity: q.deals.length === 0 ? 0.4 : 1 }}
            >📥 Export XLSX</button>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 6 }}>✕</button>
          </div>
        </div>

        {/* Quarter tabs */}
        <div style={{ display: "flex", gap: 4, padding: "10px 20px", background: "#fff", borderBottom: "1px solid #e4e4e4" }}>
          {[1, 2, 3, 4].map(qn => {
            const isCurrent = qn === currentQuarter;
            const isFuture = qn > currentQuarter;
            const active = qn === selectedQ;
            return (
              <button
                key={qn}
                onClick={() => !isFuture && setSelectedQ(qn)}
                disabled={isFuture}
                className="text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors"
                style={{
                  background: active ? "#e93529" : "transparent",
                  color: active ? "#fff" : isFuture ? "#ccc" : "#333",
                  border: `1px solid ${active ? "#e93529" : "#d8d8d8"}`,
                  cursor: isFuture ? "not-allowed" : "pointer",
                  opacity: isFuture ? 0.5 : 1,
                }}
              >
                Q{qn}{isCurrent && "*"}
              </button>
            );
          })}
          <span className="text-[10px] self-center ml-3" style={{ color: "#888" }}>* current quarter · {q.startLabel} — {q.endLabel}</span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {q.deals.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#888", fontSize: 13 }}>
              No deals were active during Q{selectedQ} {year}.
            </div>
          ) : (
            <>
              {/* Top-line tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Tile label="Active Deals" value={q.deals.length} sub={`${q.marketed.length} mkt · ${q.offMarket.length} off`} accent />
                <Tile label="Total SF" value={fmtSF(q.totalSF)} sub={`mkt ${fmtSF(q.mktSF)} · off ${fmtSF(q.offSF)}`} />
                <Tile label="Total Ask $" value={fmtMoney(q.totalAsk)} sub={`mkt ${fmtMoney(q.mktAsk)} · off ${fmtMoney(q.offAsk)}`} accent />
                <Tile label="Advanced (UW/LOI/DD)" value={q.advanced.length} sub={`at end of Q${selectedQ}`} />
              </div>

              {/* Deal lists grouped by stage-at-quarter-end.
                  Stages are ordered by pipeline priority (most-advanced first).
                  Deals without a resolvable end-stage fall into "Other". */}
              <p className="text-[11px] mb-2" style={{ color: "#888" }}>
                Deals grouped by <strong style={{ color: "#333" }}>stage at end of Q{selectedQ}</strong> (or as of today if the quarter is ongoing) — reflects where each deal stood at that snapshot, not necessarily where it is now.
              </p>
              {(() => {
                // Priority order: active → advanced first, then terminal stages
                // so kills sit at the bottom of the section list where you
                // expect them for a post-mortem review.
                const stageOrder = ["In Diligence", "LOI Submitted", "Underwriting", "Screening", "Sourcing", "Outreach", "Passed", "Lost", "Dead"];
                const byStage = {};
                for (const d of q.deals) {
                  const s = d._endStage || d.stage || "Other";
                  if (!byStage[s]) byStage[s] = [];
                  byStage[s].push(d);
                }
                const orderedStages = [
                  ...stageOrder.filter(s => byStage[s]),
                  ...Object.keys(byStage).filter(s => !stageOrder.includes(s)),
                ];
                return orderedStages.map(stage => {
                  const rows = byStage[stage]
                    .slice()
                    .sort((a, b) => (b.askingPrice || 0) - (a.askingPrice || 0));
                  const stageSF = rows.reduce((s, d) => s + (Number(d.squareFeet) || 0), 0);
                  const stageAsk = rows.reduce((s, d) => s + (Number(d.askingPrice) || 0), 0);
                  const isKilled = KILLED_STAGES.has(stage);
                  const stageColor = stage === "Passed" ? "#6a6a6a"
                                    : stage === "Lost" ? "#c42d22"
                                    : stage === "Dead" ? "#1c1c1c"
                                    : "#333";
                  return (
                    <div key={stage} className="rounded-lg overflow-hidden mb-4" style={{ background: "#fff", border: "1px solid #e4e4e4" }}>
                      <div style={{ padding: "8px 14px", background: "#f4f4f4", borderBottom: "1px solid #e4e4e4", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                        <div className="flex items-baseline gap-2">
                          <span style={{ fontSize: 10, fontWeight: 800, color: stageColor, textTransform: "uppercase", letterSpacing: 0.6 }}>
                            {stage} ({rows.length})
                          </span>
                          <span style={{ fontSize: 9, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>
                            {isKilled ? `killed in Q${selectedQ}` : `@ End of Q${selectedQ}`}
                          </span>
                        </div>
                        <span style={{ fontSize: 10, color: "#666" }}>
                          {fmtSF(stageSF)} · {fmtMoney(stageAsk)} ask
                        </span>
                      </div>
                      {isKilled ? (
                        // Killed section: 6-column grid with a chevron + reason column.
                        // Rows click to expand inline instead of navigating.
                        <>
                          <div style={{ display: "grid", gridTemplateColumns: "20px 1.4fr 1.6fr 0.6fr 0.8fr 1.2fr", padding: "6px 14px", background: "#f4f4f4", borderBottom: "1px solid #e4e4e4", fontSize: 9, fontWeight: 800, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>
                            <span></span><span>Deal</span><span>Address</span><span>SF</span><span>Ask $</span><span>Reason</span>
                          </div>
                          {rows.map((d, i) => {
                            const name = parseDealName(d.sourceNotes) || d.addresses?.[0]?.split(",")[0] || "(unnamed)";
                            const stripe = i % 2 === 1;
                            const isOpen = expandedDealId === d.id;
                            const comp = competitionByDeal[d.id];
                            return (
                              <div key={d.id} style={{ borderBottom: i < rows.length - 1 ? "1px solid #f4f4f4" : "none" }}>
                                <button
                                  type="button"
                                  onClick={() => setExpandedDealId(isOpen ? null : d.id)}
                                  style={{ width: "100%", textAlign: "left", display: "grid", gridTemplateColumns: "20px 1.4fr 1.6fr 0.6fr 0.8fr 1.2fr", padding: "6px 14px", fontSize: 11, color: "#333", background: stripe ? "#fafafa" : "#fff", border: "none", cursor: "pointer", alignItems: "center", fontFamily: "'Montserrat', sans-serif" }}
                                >
                                  <span style={{ color: "#e93529", fontWeight: 800, display: "inline-block", transition: "transform .12s ease", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>▸</span>
                                  <span className="font-semibold truncate">{name}</span>
                                  <span className="truncate" style={{ color: "#888" }}>{d.addresses?.[0] || "—"}</span>
                                  <span>{d.squareFeet ? Number(d.squareFeet).toLocaleString() : "—"}</span>
                                  <span style={{ fontWeight: d.askingPrice ? 700 : 400, color: d.askingPrice ? "#333" : "#888" }}>{d.askingPrice ? fmtMoney(d.askingPrice) : "—"}</span>
                                  <span className="truncate" style={{ color: "#333", fontStyle: d.killedReason ? "normal" : "italic" }}>{d.killedReason || "no reason recorded"}</span>
                                </button>
                                {isOpen && (
                                  <div style={{ padding: "10px 14px 14px 34px", background: "#fff", borderTop: "1px solid #f4f4f4", fontSize: 12 }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: "3px 12px", marginBottom: 8 }}>
                                      <span style={{ fontSize: 9, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.6 }}>Reason</span>
                                      <span style={{ color: "#333" }}>{d.killedReason || "—"}</span>
                                      {d.killedAt && (<>
                                        <span style={{ fontSize: 9, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.6 }}>Date</span>
                                        <span style={{ color: "#666" }}>{new Date(d.killedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                                      </>)}
                                      {d.killedNarrative && (<>
                                        <span style={{ fontSize: 9, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.6 }}>Narrative</span>
                                        <span style={{ color: "#333", whiteSpace: "pre-wrap" }}>{d.killedNarrative}</span>
                                      </>)}
                                    </div>
                                    {comp && (
                                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #e4e4e4" }}>
                                        <p style={{ fontSize: 9, fontWeight: 800, color: "#888", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Competition Detail</p>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 24px", fontSize: 11 }}>
                                          <KV k="Bidders" v={comp.num_bidders_estimated ?? "—"} />
                                          <KV k="Best & Final" v={comp.made_best_and_final === true ? "Yes" : comp.made_best_and_final === false ? "No" : "—"} />
                                          <KV k="Our Bid $/SF" v={comp.our_final_bid_psf ? `$${Number(comp.our_final_bid_psf).toFixed(2)}` : "—"} />
                                          <KV k="Our Bid Total" v={comp.our_final_bid_total ? fmtMoney(comp.our_final_bid_total) : "—"} />
                                          <KV k="Winning $/SF" v={comp.winning_bid_psf ? `$${Number(comp.winning_bid_psf).toFixed(2)}` : "—"} accent />
                                          <KV k="Winning Total" v={comp.winning_bid_total ? fmtMoney(comp.winning_bid_total) : "—"} accent />
                                          <KV k="Δ to Winner ($/SF)" v={comp.delta_to_winner_psf != null ? `$${Number(comp.delta_to_winner_psf).toFixed(2)}` : "—"} />
                                          <KV k="Δ %" v={comp.delta_pct != null ? `${Number(comp.delta_pct).toFixed(1)}%` : "—"} />
                                          <KV k="Winner" v={comp.winner_name || "—"} />
                                        </div>
                                        {comp.broker_debrief_notes && (
                                          <div style={{ marginTop: 8 }}>
                                            <p style={{ fontSize: 9, fontWeight: 800, color: "#888", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 }}>Broker Debrief</p>
                                            <p style={{ color: "#333", whiteSpace: "pre-wrap", fontSize: 11 }}>{comp.broker_debrief_notes}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <div style={{ marginTop: 10 }}>
                                      <a href={`/deal/${d.id}`} style={{ fontSize: 10, fontWeight: 700, color: "#e93529", textDecoration: "none" }}>
                                        Open deal →
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        // Non-killed sections keep the original click-to-navigate rows.
                        <>
                          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 2fr 0.7fr 0.9fr 0.7fr", padding: "6px 14px", background: "#f4f4f4", borderBottom: "1px solid #e4e4e4", fontSize: 9, fontWeight: 800, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>
                            <span>Deal</span><span>Address</span><span>SF</span><span>Ask $</span><span>Sourcing</span>
                          </div>
                          {rows.map((d, i) => {
                            const name = parseDealName(d.sourceNotes) || d.addresses?.[0]?.split(",")[0] || "(unnamed)";
                            const stripe = i % 2 === 1;
                            return (
                              <a key={d.id} href={`/deal/${d.id}`}
                                style={{ display: "grid", gridTemplateColumns: "1.5fr 2fr 0.7fr 0.9fr 0.7fr", padding: "6px 14px", borderBottom: i < rows.length - 1 ? "1px solid #f4f4f4" : "none", fontSize: 11, color: "#333", background: stripe ? "#fafafa" : "#fff", textDecoration: "none", alignItems: "center" }}>
                                <span className="font-semibold truncate">{name}</span>
                                <span className="truncate" style={{ color: "#888" }}>{d.addresses?.[0] || "—"}</span>
                                <span>{d.squareFeet ? Number(d.squareFeet).toLocaleString() : "—"}</span>
                                <span style={{ fontWeight: d.askingPrice ? 700 : 400, color: d.askingPrice ? "#333" : "#888" }}>{d.askingPrice ? fmtMoney(d.askingPrice) : "—"}</span>
                                <span style={{ color: d.marketed ? "#e93529" : "#888", fontWeight: d.marketed ? 700 : 400 }}>{d.marketed ? "On-Mkt" : "Off-Mkt"}</span>
                              </a>
                            );
                          })}
                        </>
                      )}
                    </div>
                  );
                });
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
